import { CaptionsRenderer } from '@sparemin/text-effects';
import React, { useCallback } from 'react';

import useThrottledIntegerEmit from 'hooks/useThrottledIntegerEmit';

import {
  AnimationConfigGetterParams,
  getAdvancedAnimationPreviewConfig,
} from '../extensions/AnimationsTab';
import { useTextOverlay } from '../state/TextOverlayProvider';
import { scaleEditorDataSize } from '../style-transform-utils';
import { EditorDataV2, Size } from '../types';
import { TextOverlayResizeObserver } from './useTextOverlayResizeDelta';

interface UseTextOverlayCaptionsResizeConfig {
  rendererRef: React.MutableRefObject<CaptionsRenderer | undefined>;
  isResizingRef: React.MutableRefObject<boolean>;
  pickedObservableConfig: AnimationConfigGetterParams;
}

interface ResizeContext {
  pickedObservableConfig: AnimationConfigGetterParams;
  draftEditorData: EditorDataV2 | undefined;
  canvasSize: Size;
}

/**
 * Encapsulates the resize lifecycle for the text overlay captions renderer.
 * The returned observer is a thin dispatcher; each lifecycle phase is
 * implemented by a dedicated handler.
 */
const useTextOverlayCaptionsResize = ({
  rendererRef,
  isResizingRef,
  pickedObservableConfig,
}: UseTextOverlayCaptionsResizeConfig): TextOverlayResizeObserver => {
  const { canvasSize, draftEditorData } = useTextOverlay();

  // Bundles every latest value the resize handlers need into a single ref so
  // the throttled emit callback (and other handlers with empty deps) stays
  // stable while still reading post-commit state.
  const resizeContextRef = React.useRef<ResizeContext>({
    pickedObservableConfig,
    draftEditorData,
    canvasSize,
  });

  resizeContextRef.current = {
    pickedObservableConfig,
    draftEditorData,
    canvasSize,
  };

  const preResizeUnscaledSizeRef = React.useRef<Size | null>(null);

  /**
   * Pushes a renderer config update derived from the latest picked observable.
   * When `boxSizeOverride` is provided it replaces the latest boxSize (used during
   * live resize); otherwise the canonical post-commit boxSize is used.
   */
  const applyConfigUpdate = useCallback(
    (boxSizeOverride?: Size): void => {
      const { pickedObservableConfig: latest } = resizeContextRef.current;
      const boxSize = boxSizeOverride ?? latest.boxSize;

      requestAnimationFrame(() => {
        rendererRef.current?.updateConfig(
          getAdvancedAnimationPreviewConfig(
            {
              advancedAnimation: latest.advancedAnimation,
              boxSize,
              canvasSize: latest.canvasSize,
              textStyles: latest.textStyles,
              textValue: latest.textValue,
            },
            isResizingRef.current,
          ),
        );
      });
    },
    [isResizingRef, rendererRef],
  );

  const applyLiveBoxHeight = useCallback(
    (boxHeightPx: number): void => {
      const { pickedObservableConfig: latest } = resizeContextRef.current;

      applyConfigUpdate({
        height: boxHeightPx,
        width: latest.boxSize.width,
      });
    },
    [applyConfigUpdate],
  );

  const liveResize = useThrottledIntegerEmit({
    onEmit: applyLiveBoxHeight,
  });

  const handleResizeStart = useCallback((): void => {
    const { draftEditorData: latestEditorData } = resizeContextRef.current;

    isResizingRef.current = true;
    preResizeUnscaledSizeRef.current = {
      height: latestEditorData?.getIn(['size', 'height']),
      width: latestEditorData?.getIn(['size', 'width']),
    };
    rendererRef.current?.getAnimator().reset?.();
    rendererRef.current?.getAnimator().pause?.();
  }, [isResizingRef, rendererRef]);

  const handleLiveResize = useCallback(
    (resizeDelta: Size | undefined): void => {
      const pre = preResizeUnscaledSizeRef.current;

      if (!pre || !resizeDelta) {
        return;
      }

      const {
        draftEditorData: latestEditorData,
        canvasSize: latestCanvasSize,
      } = resizeContextRef.current;

      if (!latestEditorData) {
        return;
      }

      const liveBoxSize = scaleEditorDataSize(
        {
          height: pre.height + (resizeDelta.height ?? 0),
          width: pre.width + (resizeDelta.width ?? 0),
        },
        latestEditorData,
        latestCanvasSize,
      );
      liveResize.update(liveBoxSize.height);
    },
    [liveResize],
  );

  const handleResizeStop = useCallback((): void => {
    isResizingRef.current = false;
    preResizeUnscaledSizeRef.current = null;
    liveResize.reset();
    applyConfigUpdate();
  }, [applyConfigUpdate, isResizingRef, liveResize]);

  return useCallback<TextOverlayResizeObserver>(
    (resizeDelta, opts) => {
      switch (opts.type) {
        case 'start':
          handleResizeStart();
          break;

        case 'resize':
          handleLiveResize(resizeDelta);
          break;

        case 'stop':
          handleResizeStop();
          break;

        default:
          break;
      }
    },
    [handleLiveResize, handleResizeStart, handleResizeStop],
  );
};

export default useTextOverlayCaptionsResize;
